import type { MockedResponse } from "@apollo/client/testing";
import { COMMISSION_INGEST_BATCHES_QUERY } from "../ingest/queries";
import type { CommissionIngestBatchNode, CommissionIngestReviewLine } from "../ingest/types";
import { commissionCampaignFixtures } from "./fixtures";
import { commissionIngestBatchFixtures, commissionIngestReviewLineFixtures } from "./ingestFixtures";
import { buildConnection } from "./connection";
import { createCatalogMock, withTypenames, type MockResolver } from "./mockUtils";
import { runtimeCounters, runtimeIngestBatches, runtimeReviewLines } from "./runtimeState";

function withIngestCampaignTypenames(batch: CommissionIngestBatchNode) {
  return {
    __typename: "BackofficeCommissionIngestBatch",
    ...batch,
    campaign: {
      __typename: "BackofficeCommissionCampaign",
      ...batch.campaign,
      partner: withTypenames(batch.campaign.partner, "BackofficeCommissionPartner"),
      program: withTypenames(batch.campaign.program, "BackofficeCommissionProgramSummary")
    }
  };
}

function buildIngestBatchesMockData() {
  const nodes = [...commissionIngestBatchFixtures, ...Object.values(runtimeIngestBatches)].map(
    withIngestCampaignTypenames
  );

  return {
    commissionIngestBatches: buildConnection("BackofficeCommissionIngestBatchConnection", nodes)
  };
}

function promoteCompilingIngestBatch(batchId: string, batch: CommissionIngestBatchNode) {
  if (batch.status !== "COMPILING") {
    return batch;
  }

  const reviewLines = buildCompiledReviewLines([
    { pgaId: "12345678", amountCents: 12500 },
    { pgaId: "87654321", amountCents: 9800 }
  ]);
  runtimeReviewLines[batchId] = reviewLines;
  const ready: CommissionIngestBatchNode = {
    ...batch,
    status: "READY",
    linesVersion: batch.linesVersion + 1
  };
  runtimeIngestBatches[batchId] = ready;
  return ready;
}

function buildIngestBatchMockData(batchId: string) {
  const fixture = commissionIngestBatchFixtures.find((batch) => batch.id === batchId);
  const runtime = runtimeIngestBatches[batchId];
  const batch = runtime ? promoteCompilingIngestBatch(batchId, runtime) : fixture;
  if (!batch) {
    return { commissionIngestBatch: null };
  }

  const reviewLines = (commissionIngestReviewLineFixtures[batchId] ?? runtimeReviewLines[batchId] ?? []).map(
    (line) => ({
      __typename: "BackofficeCommissionIngestReviewLine",
      ...line,
      issues: line.issues.map((issue) => ({ __typename: "BackofficeCommissionIngestReviewLineIssue", ...issue }))
    })
  );

  return {
    commissionIngestBatch: {
      ...withIngestCampaignTypenames(batch),
      reviewLines
    }
  };
}

function buildCompiledReviewLines(
  rows: Array<{ pgaId: string; amountCents: number; crmFacilityId?: string; sourceRowReference?: string }>
): CommissionIngestReviewLine[] {
  return rows.map((row, index) => {
    const blocked = row.pgaId === "11223344";
    const review = row.pgaId === "87654321";

    return {
      id: `row-${index + 1}`,
      pgaId: row.pgaId,
      amountCents: row.amountCents,
      crmFacilityId: row.crmFacilityId ?? null,
      sourceRowReference: row.sourceRowReference ?? null,
      confidence: blocked ? "BLOCKED" : review ? "REVIEW" : "ELIGIBLE",
      authorizedFacilityName: blocked ? null : "Oak Hills Golf Club",
      issues: blocked
        ? [{ code: "NOT_ENROLLED", message: "Member is not enrolled in this program." }]
        : review
          ? [{ code: "FACILITY_MISMATCH", message: "Facility does not match FAA on file." }]
          : []
    };
  });
}

export const commissionIngestBatchesApolloMock = createCatalogMock(
  COMMISSION_INGEST_BATCHES_QUERY,
  buildIngestBatchesMockData()
);

export const ingestApolloMocks: MockedResponse[] = [commissionIngestBatchesApolloMock];

export const ingestMockResults: Record<string, Record<string, unknown>> = {
  CommissionIngestBatches: buildIngestBatchesMockData()
};

export const ingestVariableMockResolvers: Record<string, MockResolver> = {
  CommissionIngestBatches: () => buildIngestBatchesMockData(),
  CommissionIngestBatch: (variables) => buildIngestBatchMockData(String(variables.id)),
  CreateCommissionIngestBatch: (variables) => {
    runtimeCounters.ingestBatch += 1;
    const campaign = commissionCampaignFixtures.find((node) => node.id === String(variables.campaignId));
    const batch: CommissionIngestBatchNode = {
      id: `commission-ingest-batch-runtime-${runtimeCounters.ingestBatch}`,
      status: "COMPILING",
      linesVersion: 1,
      sourceChecksum: `runtime-${runtimeCounters.ingestBatch}`,
      createdAt: new Date().toISOString(),
      campaign: {
        id: campaign?.id ?? "commission-campaign-2026-acme",
        editionYear: campaign?.editionYear ?? 2026,
        partner: campaign?.partner ?? { id: "commission-partner-acme", name: "Acme Golf Sponsors" },
        program: campaign?.program ?? { id: "commission-program-grp", name: "Golf Retirement Plus" }
      }
    };
    runtimeIngestBatches[batch.id] = batch;

    return {
      commissionCreateIngestBatch: {
        success: true,
        message: "Batch created.",
        ingestBatch: withIngestCampaignTypenames(batch)
      }
    };
  },
  CompileCommissionIngest: (variables) => {
    const batchId = String(variables.batchId);
    const existing =
      runtimeIngestBatches[batchId] ?? commissionIngestBatchFixtures.find((batch) => batch.id === batchId);
    const rows = (variables.rows as Array<{ pgaId: string; amountCents: number }>) ?? [];
    const reviewLines = buildCompiledReviewLines(rows);
    runtimeReviewLines[batchId] = reviewLines;

    const compiled: CommissionIngestBatchNode = {
      ...(existing ?? commissionIngestBatchFixtures[0]),
      id: batchId,
      status: "READY",
      linesVersion: (existing?.linesVersion ?? 1) + 1
    };
    runtimeIngestBatches[batchId] = compiled;

    return {
      commissionCompileIngest: {
        success: true,
        message: "Batch compiled.",
        ingestBatch: withIngestCampaignTypenames(compiled)
      }
    };
  },
  ConfirmCommissionIngest: (variables) => {
    const batchId = String(variables.batchId);
    const existing =
      runtimeIngestBatches[batchId] ?? commissionIngestBatchFixtures.find((batch) => batch.id === batchId);
    const confirmed: CommissionIngestBatchNode = {
      ...(existing ?? commissionIngestBatchFixtures[0]),
      id: batchId,
      status: "CONFIRMED",
      linesVersion: (existing?.linesVersion ?? 1) + 1
    };
    runtimeIngestBatches[batchId] = confirmed;

    return {
      commissionConfirmIngest: {
        success: true,
        message: "Batch confirmed.",
        ingestBatch: withIngestCampaignTypenames(confirmed)
      }
    };
  }
};
